/**
 * Updates document meta tags for tenant branding
 * Called immediately when tenant config is loaded, before first render
 */

const DEEDSTACK_DEFAULTS = {
  title: 'DeedStack™ - Land Financing Platform',
  description: 'DeedStack™ - White-label land financing platform for land sellers. Manage loans, payments, and customers with ease.',
  favicon: '/images/deedstack-logos/logo-icon-final.svg',
  themeColor: '#1a365d'
};

export function updateMetaTags(tenant, isMarketingSite = false) {
  // Run synchronously to update before paint
  try {
    // DEBUG: Log what we received
    console.log('[updateMetaTags] Called with:', {
      isMarketingSite,
      tenant,
      logos: tenant?.logos,
      logoIcon: tenant?.logos?.icon,
      logoFull: tenant?.logos?.full,
      logoHorizontal: tenant?.logos?.horizontal
    });

    if (isMarketingSite || !tenant) {
      // Reset to DeedStack defaults
      console.log('[updateMetaTags] Using DeedStack defaults');
      setTitle(DEEDSTACK_DEFAULTS.title);
      setDescription(DEEDSTACK_DEFAULTS.description);
      setFavicon(DEEDSTACK_DEFAULTS.favicon);
      setThemeColor(DEEDSTACK_DEFAULTS.themeColor);
      return;
    }

    // Tenant-specific branding
    const companyName = tenant.companyName || 'Land Financing';
    const tagline = tenant.tagline || 'Land Financing';

    // Title
    setTitle(`${companyName} - ${tagline}`);

    // Description
    const description = tenant.metaDescription ||
      `${companyName} - Affordable land financing with easy monthly payments. Browse available properties and start your land ownership journey today.`;
    setDescription(description);

    // Favicon. A favicon the tenant uploaded on purpose wins outright: it is
    // the only value here chosen to be an icon rather than inferred from a
    // horizontal logo, which is usually the wrong shape for a 16px square.
    // The logo search below stays as the fallback for tenants who have not
    // uploaded one, so nothing regresses for them.
    const logos = tenant.logos || {};
    const isCustomUrl = (url) => url && (url.includes('cloudinary') || url.includes('https://')) && !url.startsWith('/images/');

    if (isCustomUrl(logos.favicon)) {
      setFavicon(logos.favicon);
      if (tenant.primaryColor) {
        setThemeColor(tenant.primaryColor);
      }
      return;
    }

    let tenantLogo = null;
    if (isCustomUrl(logos.full)) {
      tenantLogo = logos.full;
    } else if (isCustomUrl(logos.horizontal)) {
      tenantLogo = logos.horizontal;
    } else if (isCustomUrl(logos.icon)) {
      tenantLogo = logos.icon;
    }

    console.log('[updateMetaTags] Logo candidates:', { full: logos.full, horizontal: logos.horizontal, icon: logos.icon });
    console.log('[updateMetaTags] tenantLogo selected:', tenantLogo);

    if (tenantLogo) {
      console.log('[updateMetaTags] Setting favicon to tenant logo:', tenantLogo);
      setFavicon(tenantLogo);
    } else {
      console.log('[updateMetaTags] No custom logo found, using default favicon');
      setFavicon(DEEDSTACK_DEFAULTS.favicon);
    }

    // Theme color - use tenant's primary color
    if (tenant.primaryColor) {
      setThemeColor(tenant.primaryColor);
    }

  } catch (err) {
    console.error('Error updating meta tags:', err);
  }
}

function setTitle(title) {
  document.title = title;
}

function setDescription(description) {
  const meta = document.getElementById('meta-description');
  if (meta) {
    meta.setAttribute('content', description);
  }
}

function setFavicon(url) {
  console.log('[setFavicon] Setting favicon to:', url);

  // Update main favicon
  const favicon = document.getElementById('dynamic-favicon');
  console.log('[setFavicon] Found dynamic-favicon element:', !!favicon);
  if (favicon) {
    favicon.href = url;
    console.log('[setFavicon] Set dynamic-favicon.href to:', favicon.href);
  }

  // Update apple touch icon
  const appleTouchIcon = document.getElementById('apple-touch-icon');
  if (appleTouchIcon) {
    appleTouchIcon.href = url;
  }

  // Also try to update any other favicon links
  const allFavicons = document.querySelectorAll("link[rel*='icon']");
  console.log('[setFavicon] Found', allFavicons.length, 'favicon links total');
  allFavicons.forEach(link => {
    if (link.id !== 'dynamic-favicon') {
      link.href = url;
    }
  });
}

function setThemeColor(color) {
  const meta = document.getElementById('theme-color');
  if (meta) {
    meta.setAttribute('content', color);
  }
}

export default updateMetaTags;
